import React, { useEffect, useState, useRef } from 'react';
import { View, Text, ActivityIndicator, ScrollView, StyleSheet } from 'react-native';
import { get } from '../../api/client';
import {
  BLUE, BLUE_DARK, BLUE_LIGHT, BG, CARD, TEXT, TEXT_MUTED, DIVIDER,
} from '../../theme/colors';

const CLASS_LABELS: Record<string, string> = {
  CLASS_1: 'Klasse 1', CLASS_2: 'Klasse 2', CLASS_3: 'Klasse 3',
  CLASS_4: 'Klasse 4', CLASS_5: 'Klasse 5',
};

function medalLabel(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `${rank}.`;
}

export default function ResultsScreen({ route }: any) {
  const competitionId: number = route.params.competitionId;
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchScores = async () => {
    try {
      const res = await get<any>(`/competitions/${competitionId}/scores`);
      setData(res);
    } catch (e: any) {
      console.warn('fetch scores failed', e.message);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchScores().finally(() => setLoading(false));
    pollRef.current = setInterval(fetchScores, 10000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [competitionId]);

  if (loading) {
    return <View style={s.center}><ActivityIndicator size="large" color={BLUE} /></View>;
  }

  const categories: any[] = data?.categories ?? [];

  if (categories.length === 0) {
    return (
      <View style={[s.center, { backgroundColor: BG }]}>
        <Text style={s.emptyText}>Ingen resultater tilgjengelig ennå.</Text>
        <Text style={s.emptySubText}>Lag vises her når de er ferdige med alle runder.</Text>
      </View>
    );
  }

  return (
    <ScrollView style={s.bg} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={s.topNote}>
        <Text style={s.topNoteText}>Oppdateres automatisk – kun fullførte lag vises</Text>
      </View>

      {categories.map((cat: any) => {
        const noScoring = cat.category === 'CLASS_1' || cat.category === 'CLASS_2';
        return (
          <View key={cat.category} style={s.categorySection}>
            <Text style={s.categoryTitle}>
              {CLASS_LABELS[cat.category] ?? cat.category}
            </Text>

            {noScoring ? (
              <View style={s.noScoringRow}>
                <Text style={s.noScoringText}>
                  Klasse 1 og 2 har ikke poengbedømmelse.
                </Text>
              </View>
            ) : (cat.teams ?? []).map((team: any) => (
              <View
                key={team.team_id}
                style={[s.teamRow, team.rank === 1 && s.teamRowFirst]}
              >
                <Text style={[s.rank, team.rank <= 3 && s.rankMedal]}>
                  {medalLabel(team.rank)}
                </Text>
                <View style={s.teamInfo}>
                  <Text style={[s.teamName, team.rank === 1 && s.teamNameFirst]}>
                    {team.team_name}
                  </Text>
                  <Text style={s.clubName}>{team.club_name}</Text>
                </View>
                <View style={s.scoreBlock}>
                  <Text style={[s.scoreMain, team.rank === 1 && s.scoreMainFirst]}>
                    {team.score_with_bonus != null
                      ? team.score_with_bonus.toFixed(3)
                      : '–'}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        );
      })}
    </ScrollView>
  );
}

const s = StyleSheet.create({
  bg:     { flex: 1, backgroundColor: BG },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: BG, paddingHorizontal: 32 },
  emptyText:    { fontSize: 16, color: '#888', textAlign: 'center' },
  emptySubText: { fontSize: 13, color: TEXT_MUTED, textAlign: 'center', marginTop: 8 },

  topNote:     { paddingHorizontal: 16, paddingTop: 14, paddingBottom: 4 },
  topNoteText: { fontSize: 12, color: TEXT_MUTED, textAlign: 'center' },

  categorySection: {
    marginHorizontal: 16, marginTop: 18,
    backgroundColor: CARD, borderRadius: 12, overflow: 'hidden',
    shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 4, shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  categoryTitle: {
    fontSize: 15, fontWeight: '700', color: BLUE_DARK,
    paddingHorizontal: 14, paddingVertical: 10,
    backgroundColor: BLUE_LIGHT,
    borderBottomWidth: 1, borderBottomColor: DIVIDER,
  },

  teamRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 14, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: DIVIDER,
  },
  teamRowFirst: { backgroundColor: '#FFFDF0' },

  rank:      { width: 34, fontSize: 15, color: TEXT_MUTED, textAlign: 'center' },
  rankMedal: { fontSize: 20 },

  teamInfo:      { flex: 1, marginLeft: 6 },
  teamName:      { fontSize: 15, fontWeight: '600', color: TEXT },
  teamNameFirst: { fontWeight: '700', color: BLUE_DARK },
  clubName:      { fontSize: 12, color: TEXT_MUTED, marginTop: 2 },

  scoreBlock:     { alignItems: 'flex-end' },
  scoreMain:      { fontSize: 18, fontWeight: '700', color: TEXT },
  scoreMainFirst: { color: BLUE_DARK, fontSize: 20 },

  noScoringRow: { paddingHorizontal: 14, paddingVertical: 14 },
  noScoringText: { fontSize: 13, color: TEXT_MUTED, fontStyle: 'italic', lineHeight: 19 },
});
